import React from 'react';
import { Check, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WizardStep } from '@/store/webinar/wizardStore';

interface WizardStepperProps {
  steps: WizardStep[];
  currentStep: number;
  onStepClick: (stepId: number) => void;
}

export function WizardStepper({ steps, currentStep, onStepClick }: WizardStepperProps) {
  const isStepAccessible = (stepId: number) => {
    // Can access current step, completed steps, or next immediate step
    if (stepId === currentStep) return true;
    if (steps[stepId - 1]?.completed) return true;
    if (stepId === currentStep + 1) return true;
    
    // Check if all previous steps are completed
    for (let i = 0; i < stepId - 1; i++) {
      if (!steps[i].completed && i + 1 !== currentStep) return false;
    }
    return true;
  };

  return (
    <div className="border-b bg-card">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin pb-2">
          {steps.map((step) => {
            const isActive = step.id === currentStep;
            const isCompleted = step.completed;
            const isAccessible = isStepAccessible(step.id);
            const isLocked = !isAccessible;

            return (
              <button
                key={step.id}
                onClick={() => isAccessible && onStepClick(step.id)}
                disabled={isLocked}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap',
                  'border-2',
                  isActive && 'bg-primary text-primary-foreground border-primary',
                  isCompleted && !isActive && 'bg-green-50 text-green-800 border-green-200 hover:bg-green-100',
                  !isActive && !isCompleted && !isLocked && 'bg-background border-border hover:border-primary',
                  isLocked && 'bg-muted text-muted-foreground border-muted cursor-not-allowed opacity-50'
                )}
              >
                {isCompleted ? (
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-green-600 text-white">
                    <Check className="h-3 w-3" />
                  </div>
                ) : isLocked ? (
                  <div className="flex items-center justify-center w-5 h-5">
                    <Lock className="h-3 w-3" />
                  </div>
                ) : (
                  <div className={cn(
                    "flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold",
                    isActive ? "bg-primary-foreground text-primary" : "bg-muted"
                  )}>
                    {step.id}
                  </div>
                )}
                <span>{step.title}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
